import React, { useMemo, useState } from 'react';
import {
  Autocomplete, TextField, Box, Typography, CircularProgress, Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import EventIcon from '@mui/icons-material/Event';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { useAuth } from './AuthGate';
import { sfOpportunityUrl, smartTicketUrl } from '../utils/externalLinks';
import { useSuggestionsPage } from '../hooks/useSuggestions';
import { eventLink } from '../utils/eventNav';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const GROUP_META = {
  person: { label: 'People', icon: <PersonIcon fontSize="small" /> },
  workshop: { label: 'Workshops', icon: <SchoolIcon fontSize="small" /> },
  event: { label: 'Events', icon: <EventIcon fontSize="small" /> },
  tag: { label: 'Tags', icon: <LocalOfferIcon fontSize="small" /> },
  opportunity: { label: 'Opportunities', icon: <RequestQuoteIcon fontSize="small" /> },
  smart_ticket: { label: 'SMART Tickets', icon: <ConfirmationNumberIcon fontSize="small" /> },
};

function toOptions(data) {
  if (!data) return [];
  const persons = (data.persons || []).map((p) => ({
    type: 'person', id: p.id, label: p.fullname || p.email || `Person ${p.id}`, sub: p.email,
  }));
  const workshops = (data.workshops || []).map((w) => ({
    type: 'workshop', id: w.id, label: w.name, sub: null, tags: w.tags || [], matchedOn: w.matched_on,
  }));
  const events = (data.events || []).map((e) => ({
    type: 'event', id: e.id, label: e.title, sub: e.start_datetime ? e.start_datetime.slice(0, 10) : null,
  }));
  const tags = (data.tags || []).map((t) => ({
    type: 'tag', id: t.id, label: t.name, sub: `tag · ${t.category}`, category: t.category,
  }));
  const opportunities = (data.opportunities || []).map((o) => ({
    type: 'opportunity', id: o.id, label: o.oppname || o.id, sub: o.account_name || o.stage_name,
    url: sfOpportunityUrl(o.id),
  }));
  const smartTickets = (data.smart_tickets || []).map((t) => ({
    type: 'smart_ticket', id: t.id, label: t.source_ticket_id || `Ticket ${t.id}`, sub: t.summary,
    url: smartTicketUrl(t.source_ticket_id),
  }));
  return [...persons, ...workshops, ...events, ...tags, ...opportunities, ...smartTickets];
}

/**
 * GlobalSearch — debounced server-side typeahead over people/workshops/events.
 *
 * First debounced-server-query search pattern in this codebase (existing
 * Autocomplete usages, e.g. TagAdmin.jsx, are all client-side over preloaded
 * arrays). Gated on the `search:read` permission — renders nothing without it,
 * matching how the rest of the app hides ungranted UI rather than disabling it.
 */
export default function GlobalSearch({ variant = 'inline', onNavigate }) {
  const { canRead } = useAuth();
  const [inputValue, setInputValue] = useState('');
  const hasAccess = canRead('search');
  const { items: options, loading } = useSuggestionsPage(
    `${API_BASE_URL}/search`, inputValue.trim(), {}, { minChars: 2, enabled: hasAccess, select: toOptions },
  );

  const groupedOptions = useMemo(
    () => [...options].sort((a, b) => a.type.localeCompare(b.type)),
    [options],
  );

  if (!hasAccess) return null;

  // Every result opens in a new tab — the search dropdown stays in place in the
  // current tab (matching the "keep browsing while I check this" use case) rather
  // than navigating it away. Event hits go to the registrations list, not the
  // edit page: they're title matches, not registrant matches, so no search term
  // carries over (see PersonProfile's own registration links for the filtered case).
  const handleSelect = (_e, option) => {
    if (!option) return;
    setInputValue('');
    let path;
    if (option.type === 'person') path = `/participants/${option.id}`;
    else if (option.type === 'workshop') path = `/workshops/${option.id}`;
    else if (option.type === 'tag') path = `/admin/tags?category=${encodeURIComponent(option.category || '')}&filter=${option.id}`;
    else if (option.type === 'opportunity' || option.type === 'smart_ticket') path = option.url;
    else path = eventLink(option.id, 'registrants');
    if (!path) return;
    window.open(path, '_blank', 'noopener,noreferrer');
    onNavigate?.();
  };

  return (
    <Autocomplete
      options={groupedOptions}
      groupBy={(option) => GROUP_META[option.type]?.label || option.type}
      getOptionLabel={(option) => option.label || ''}
      filterOptions={(x) => x} // server already filtered — don't re-filter client-side
      loading={loading}
      open={inputValue.trim().length >= 2}
      inputValue={inputValue}
      onInputChange={(_e, value) => setInputValue(value)}
      onChange={handleSelect}
      isOptionEqualToValue={(a, b) => a.type === b.type && a.id === b.id}
      renderOption={(props, option) => (
        // aria-label overrides the computed accessible name — without it, the
        // sub-text (email/date/category) concatenates onto the label with no
        // separator ("FortiADCProduct"), which silently breaks any exact-name
        // locator even though the sub-text is only meant as visual context.
        <Box component="li" {...props} key={`${option.type}-${option.id}`} aria-label={option.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {GROUP_META[option.type]?.icon}
          <Box>
            <Typography variant="body2">{option.label}</Typography>
            {option.sub && (
              <Typography variant="caption" color="text.secondary">{option.sub}</Typography>
            )}
            {option.tags?.length > 0 && (
              // tag names repeat across categories (SecOps = Competency and Pillar), so
              // every chip carries its category
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, mt: 0.25 }}>
                {option.tags.map((t) => (
                  <Chip key={t.id} size="small" variant="outlined" label={`${t.name} · ${t.category}`} sx={{ fontSize: '0.65rem', height: 18 }} />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      )}
      noOptionsText={inputValue.trim().length < 2 ? 'Type at least 2 characters' : 'No results'}
      sx={variant === 'inline' ? { width: 280, mr: 2 } : { width: '100%' }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder="Search people, workshops (title or tag), events, tags, opportunities, tickets…"
          variant={variant === 'inline' ? 'standard' : 'outlined'}
          autoFocus={variant !== 'inline'}
          // MUI spreads a top-level aria-label onto the outer FormControl div, not the
          // actual <input> — same trap as the inputProps/SelectDisplayProps MUI gotcha
          // elsewhere in this codebase. Merge into params.inputProps (lowercase, the
          // Autocomplete-supplied props for the real input) instead of replacing it.
          inputProps={{ ...params.inputProps, 'aria-label': 'Global search' }}
          InputProps={{
            ...params.InputProps,
            // OutlinedInput (the 'outlined' variant) doesn't accept disableUnderline —
            // passing it unconditionally leaks through as an unrecognized DOM attribute.
            ...(variant === 'inline' ? { disableUnderline: true } : {}),
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, opacity: 0.8 }} />,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
            sx: variant === 'inline' ? { color: 'white', '& .MuiSvgIcon-root': { color: 'white' } } : undefined,
          }}
          InputLabelProps={{ sx: variant === 'inline' ? { color: 'white' } : undefined }}
        />
      )}
    />
  );
}
